import { Event } from '@adminModels';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EventStatus } from '@klubovna/api-client';
import { toAdminEvent } from '@api/adapters/event.adapter';
import { ApiConfiguration } from '@klubovna/api-client';
import {
  eventsControllerCreate,
  EventsControllerCreate$Params,
  eventsControllerDelete,
  eventsControllerDeleteThumbnail,
  eventsControllerDeleteTitleImage,
  eventsControllerFindAllAdmin,
  EventsControllerFindAllAdmin$Params,
  eventsControllerFindBySlug,
  EventsControllerFindBySlug$Params,
  eventsControllerFindOne,
  eventsControllerUpdate,
  EventsControllerUpdate$Params,
  eventsControllerUploadThumbnail,
  EventsControllerUploadThumbnail$Params,
  eventsControllerUploadTitleImage,
  EventsControllerUploadTitleImage$Params,
} from '@klubovna/api-client';
import { CreateEventDto, EventResponse, UpdateEventDto } from '@klubovna/api-client';
import { PaginatedResponse, toPaginatedResponse } from '@klubovna/api-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface EventsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  tagIds?: string[];
  isActive?: boolean;
  private?: boolean;
  dateFrom?: string;
  dateTo?: string;
  status?: EventStatus;
  sortBy?: 'date' | 'createdAt' | 'updatedAt' | 'name' | 'name.sk' | 'name.en' | 'status';
  sortOrder?: 'ASC' | 'DESC';
}

export type CreateEventData = CreateEventDto;
export type UpdateEventData = UpdateEventDto;

@Injectable({
  providedIn: 'root',
})
export class EventsApiService {
  private http = inject(HttpClient);
  private apiConfig = inject(ApiConfiguration);

  findAll(params?: EventsQueryParams): Observable<PaginatedResponse<Event>> {
    const apiParams: EventsControllerFindAllAdmin$Params = {
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
      tagIds: params?.tagIds,
      isActive: params?.isActive,
      private: params?.private,
      dateFrom: params?.dateFrom,
      dateTo: params?.dateTo,
      status: params?.status,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    };

    return eventsControllerFindAllAdmin(this.http, this.apiConfig.rootUrl, apiParams)
      .pipe(
        map(response => toPaginatedResponse<EventResponse>(response.body)),
        map(paginated => ({
          ...paginated,
          items: paginated.items.map(toAdminEvent),
        })),
      );
  }

  findOne(id: string): Observable<Event> {
    return eventsControllerFindOne(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => toAdminEvent(response.body)));
  }

  findBySlug(slug: string): Observable<Event> {
    const params: EventsControllerFindBySlug$Params = { slug };

    return eventsControllerFindBySlug(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => toAdminEvent(response.body)));
  }

  create(eventData: CreateEventData): Observable<Event> {
    const params: EventsControllerCreate$Params = {
      body: eventData,
    };
    return eventsControllerCreate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => toAdminEvent(response.body)));
  }

  update(id: string, eventData: UpdateEventData): Observable<Event> {
    const params: EventsControllerUpdate$Params = {
      id,
      body: eventData,
    };
    return eventsControllerUpdate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => toAdminEvent(response.body)));
  }

  delete(id: string): Observable<void> {
    return eventsControllerDelete(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(() => undefined));
  }

  uploadTitleImage(id: string, file: File): Observable<Event> {
    const params: EventsControllerUploadTitleImage$Params = {
      id,
      body: { file },
    };
    return eventsControllerUploadTitleImage(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => toAdminEvent(response.body)));
  }

  uploadThumbnail(id: string, file: File): Observable<Event> {
    const params: EventsControllerUploadThumbnail$Params = {
      id,
      body: { file },
    };
    return eventsControllerUploadThumbnail(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => toAdminEvent(response.body)));
  }

  deleteTitleImage(id: string): Observable<Event> {
    return eventsControllerDeleteTitleImage(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => toAdminEvent(response.body)));
  }

  deleteThumbnail(id: string): Observable<Event> {
    return eventsControllerDeleteThumbnail(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => toAdminEvent(response.body)));
  }
}
